document.documentElement.classList.add('lc-js');

(() => {
  const rootRoute = () => window.Shopify?.routes?.root || '/';
  const formatMoney = (cents, currency) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency: currency || 'CAD'
      }).format(value);
    } catch (_error) {
      return '$' + value.toFixed(2);
    }
  };

  const inferSize = (text = '') => {
    const normalized = String(text).toLowerCase().replace(/\s+/g, '');
    if (normalized.includes('4l')) return '4l';
    if (normalized.includes('16oz')) return '16oz';
    if (normalized.includes('4oz')) return '4oz';
    if (normalized.includes('2oz')) return '2oz';
    return '';
  };

  const initializeMenu = (root = document) => {
    root.querySelectorAll('[data-lc-menu-toggle]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      const nav = document.getElementById(button.getAttribute('aria-controls'));
      if (!nav) return;
      const closedLabel = button.getAttribute('aria-label') || 'Open menu';
      const openLabel = /open/i.test(closedLabel) ? closedLabel.replace(/open/i, 'Close') : 'Close menu';
      const close = () => {
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-label', closedLabel);
        nav.classList.remove('is-open');
        document.body.classList.remove('lc-menu-open');
      };
      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', String(open));
        button.setAttribute('aria-label', open ? openLabel : closedLabel);
        nav.classList.toggle('is-open', open);
        document.body.classList.toggle('lc-menu-open', open);
      });
      nav.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
      document.addEventListener('keydown', (event) => event.key === 'Escape' && close());
    });
  };

  const initializeLocalization = (root = document) => {
    root.querySelectorAll('[data-lc-country-select]').forEach((select) => {
      if (select.dataset.initialized === 'true') return;
      select.dataset.initialized = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });
  };

  const initializeReveal = (root = document) => {
    const items = [...root.querySelectorAll('.lc-reveal:not([data-lc-reveal-ready])')];
    if (!items.length) return;
    items.forEach((item) => item.setAttribute('data-lc-reveal-ready', 'true'));
    if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: 0.06 });
    items.forEach((item) => observer.observe(item));
  };

  const normalizeSelectorData = (scope, select) => {
    [...select.options].forEach((option) => {
      const size = inferSize(option.dataset.title || option.textContent);
      if (size) option.dataset.size = size;
    });
    scope.querySelectorAll('[data-lc-format-option]').forEach((button) => {
      const option = [...select.options].find((item) => item.value === button.dataset.variantId);
      const size = inferSize(option?.dataset.title || option?.textContent || '');
      if (size) button.dataset.size = size;
    });
  };

  const syncVariantGallery = (scope, option) => {
    const gallery = scope.querySelector('[data-lc-gallery]');
    const main = gallery?.querySelector('[data-lc-gallery-main]');
    const variantThumb = gallery?.querySelector('[data-lc-variant-thumb]');
    const variantImage = option.dataset.image;
    if (!gallery || !main || !variantThumb || !variantImage) return;

    const variantId = String(option.value || '');
    const mediaId = option.dataset.mediaId || '';
    const alt = option.dataset.imageAlt || option.dataset.title || '';
    main.src = variantImage;
    if (option.dataset.imageSrcset) main.srcset = option.dataset.imageSrcset;
    else main.removeAttribute('srcset');
    main.alt = alt;
    if (mediaId) main.dataset.mediaId = mediaId;

    variantThumb.hidden = false;
    variantThumb.dataset.src = variantImage;
    variantThumb.dataset.alt = alt;
    if (mediaId) variantThumb.dataset.mediaId = mediaId;
    const thumbImage = variantThumb.querySelector('[data-lc-variant-thumb-image], img');
    if (thumbImage) {
      thumbImage.src = option.dataset.imageThumb || variantImage;
      thumbImage.removeAttribute('srcset');
      thumbImage.alt = alt;
    }

    gallery.querySelectorAll('[data-lc-gallery-support]').forEach((thumb) => {
      const variantIds = String(thumb.dataset.lcVariantIds || '').split(/\s+/).filter(Boolean);
      thumb.hidden = !(variantIds.includes('*') || variantIds.includes(variantId));
    });
    gallery.querySelectorAll('[data-lc-gallery-thumb]').forEach((thumb) => {
      const selected = thumb === variantThumb;
      thumb.classList.toggle('is-active', selected);
      thumb.setAttribute('aria-pressed', String(selected));
    });
    const strip = gallery.querySelector('.lc-product-gallery__thumbs');
    if (strip) strip.scrollLeft = 0;
  };

  const updateVariantScope = (scope, select) => {
    const option = select.options[select.selectedIndex];
    if (!option) return;
    const available = option.dataset.available === 'true';
    const price = option.dataset.price;
    const compareAt = option.dataset.compareAt;
    const currency = option.dataset.currency || 'CAD';
    const size = option.dataset.size || inferSize(option.dataset.title || option.textContent);
    const title = option.dataset.title || option.textContent.trim();

    scope.querySelectorAll('[data-lc-price]').forEach((element) => {
      element.textContent = formatMoney(price, currency);
    });
    scope.querySelectorAll('[data-lc-compare-price]').forEach((element) => {
      const show = Number(compareAt) > Number(price);
      element.textContent = show ? formatMoney(compareAt, currency) : '';
      element.hidden = !show;
    });
    scope.querySelectorAll('[data-lc-selected-title]').forEach((element) => {
      element.textContent = title;
    });
    scope.querySelectorAll('[data-lc-add-button]').forEach((button) => {
      button.disabled = !available;
      button.textContent = available ? button.dataset.availableText : button.dataset.soldOutText;
    });
    scope.querySelectorAll('[data-lc-availability]').forEach((element) => {
      element.textContent = available ? element.dataset.availableText : element.dataset.unavailableText;
      element.classList.toggle('is-available', available);
    });
    scope.querySelectorAll('[data-lc-size-option]').forEach((button) => {
      const selected = button.dataset.lcSizeOption === size;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    scope.querySelectorAll('[data-lc-format-option]').forEach((button) => {
      const selected = button.dataset.variantId === option.value;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    const variantImage = option.dataset.image;
    const variantAlt = option.dataset.imageAlt || title;
    if (variantImage) {
      scope.querySelectorAll('[data-lc-packshot-image], [data-lc-hero-image]').forEach((image) => {
        image.src = variantImage;
        image.removeAttribute('srcset');
        image.alt = variantAlt;
      });
    }
    syncVariantGallery(scope, option);
    const accelerated = scope.querySelector('.lc-accelerated-checkout');
    if (accelerated) {
      const country = scope.dataset.lcCountry || window.Shopify?.country || '';
      accelerated.hidden = !available || country !== 'CA';
    }
  };

  const replaceVariantUrl = (variantId) => {
    if (!variantId || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState(window.history.state, '', url);
  };

  const chooseSize = (scope, select, size) => {
    const formatButtons = [...scope.querySelectorAll('[data-lc-format-option]')];
    formatButtons.forEach((button) => { button.hidden = button.dataset.size !== size; });
    const matching = formatButtons.filter((button) => button.dataset.size === size);
    const glass = matching.find((button) => button.dataset.sellable === 'true' && /glass/i.test(button.textContent));
    const available = matching.find((button) => button.dataset.sellable === 'true');
    const next = glass || available || matching[0];
    if (!next) return;
    select.value = next.dataset.variantId;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    replaceVariantUrl(next.dataset.variantId);
  };

  const initializeVariantSelectors = (root = document) => {
    root.querySelectorAll('[data-lc-product-scope]').forEach((scope) => {
      if (scope.dataset.lcVariantsReady === 'true') return;
      const select = scope.querySelector('[data-lc-variant-select]');
      if (!select) return;
      scope.dataset.lcVariantsReady = 'true';
      normalizeSelectorData(scope, select);

      const params = new URLSearchParams(window.location.search);
      if (!params.has('variant')) {
        const preferred = [...select.options].find((option) => {
          const title = String(option.dataset.title || option.textContent).toLowerCase().replace(/\s+/g, '');
          return title.includes('4oz') && title.includes('glass') && option.dataset.available === 'true';
        });
        if (preferred) select.value = preferred.value;
      }

      scope.querySelectorAll('[data-lc-size-option]').forEach((button) => {
        button.addEventListener('click', () => chooseSize(scope, select, button.dataset.lcSizeOption));
      });
      scope.querySelectorAll('[data-lc-format-option]').forEach((button) => {
        button.addEventListener('click', () => {
          select.value = button.dataset.variantId;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          replaceVariantUrl(button.dataset.variantId);
        });
      });
      select.addEventListener('change', () => updateVariantScope(scope, select));
      updateVariantScope(scope, select);
    });
  };

  const cartRequestOptions = (body) => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.getSectionsToRender) {
      body.append('sections', cartDrawer.getSectionsToRender().map((section) => section.id));
      body.append('sections_url', window.location.pathname);
    }
    return { cartDrawer, options: { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body } };
  };

  const renderCart = (cartDrawer, response) => {
    if (cartDrawer?.renderContents && response.sections) {
      // Dawn's cart drawer can retain its outer `is-empty` class when the first
      // item is added asynchronously. That class hides the newly rendered line
      // items even though Shopify has already updated the subtotal and cart
      // count. Clear it before rendering the non-empty section response.
      cartDrawer.classList.remove('is-empty');
      cartDrawer.renderContents(response);
    } else window.location.assign(rootRoute() + 'cart');
  };

  const initializeProductForms = (root = document) => {
    root.querySelectorAll('[data-lc-product-form]').forEach((form) => {
      if (form.dataset.lcSubmitInitialized === 'true') return;
      form.dataset.lcSubmitInitialized = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const formId = form.getAttribute('id');
        const buttons = document.querySelectorAll(`[form="${formId}"][data-lc-add-button], #${formId} [data-lc-add-button]`);
        const status = form.querySelector('[data-lc-form-status]');
        buttons.forEach((button) => {
          button.disabled = true;
          button.dataset.originalText = button.textContent;
          button.textContent = button.dataset.loadingText;
        });
        if (status) status.textContent = '';
        try {
          const payload = new FormData(form);
          const request = cartRequestOptions(payload);
          const response = await fetch(rootRoute() + 'cart/add.js', request.options);
          const json = await response.json();
          if (!response.ok || json.status) throw new Error(json.description || 'Cart request failed');
          renderCart(request.cartDrawer, json);
        } catch (error) {
          if (status) status.textContent = error.message;
        } finally {
          buttons.forEach((button) => {
            button.disabled = false;
            button.textContent = button.dataset.originalText || button.dataset.availableText;
          });
        }
      });
    });
  };

  const addItems = async (items) => {
    const payload = new FormData();
    items.forEach((item, index) => {
      payload.append(`items[${index}][id]`, item.id);
      payload.append(`items[${index}][quantity]`, item.quantity || 1);
    });
    const request = cartRequestOptions(payload);
    const response = await fetch(rootRoute() + 'cart/add.js', request.options);
    const json = await response.json();
    if (!response.ok || json.status) throw new Error(json.description || 'Cart request failed');
    renderCart(request.cartDrawer, json);
  };

  const initializeQuickAdds = (root = document) => {
    root.querySelectorAll('[data-lc-quick-add]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', async () => {
        const original = button.textContent;
        const status = button.closest('[data-lc-format-row]')?.querySelector('[data-lc-quick-add-status]');
        if (button.dataset.available !== 'true') return;
        if (status) {
          status.textContent = '';
          status.hidden = true;
        }
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = button.dataset.loadingText || original;
        try { await addItems([{ id: button.dataset.variantId, quantity: 1 }]); }
        catch (error) {
          if (status) {
            status.textContent = error.message || 'We could not add that selection. Please try again.';
            status.hidden = false;
          }
        }
        finally {
          if (document.contains(button)) {
            button.disabled = button.dataset.available !== 'true';
            button.removeAttribute('aria-busy');
            button.textContent = original;
          }
        }
      });
    });

    root.querySelectorAll('[data-lc-bundle-add]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', async () => {
        const original = button.textContent;
        const status = button.closest('.lc-bundle-card')?.querySelector('[data-lc-bundle-status]');
        let items = [];
        try { items = JSON.parse(button.dataset.items || '[]'); } catch (_error) { items = []; }
        if (!items.length) return;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        button.textContent = button.dataset.loadingText || original;
        if (status) status.textContent = '';
        try { await addItems(items); }
        catch (_error) {
          if (status) status.textContent = button.dataset.errorText;
        } finally {
          if (document.contains(button)) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
            button.textContent = original;
          }
        }
      });
    });
  };

  const initializeGallery = (root = document) => {
    root.querySelectorAll('[data-lc-gallery]').forEach((gallery) => {
      if (gallery.dataset.initialized === 'true') return;
      gallery.dataset.initialized = 'true';
      const main = gallery.querySelector('[data-lc-gallery-main]');
      gallery.querySelectorAll('[data-lc-gallery-thumb]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!main || !button.dataset.src) return;
          main.src = button.dataset.src;
          main.removeAttribute('srcset');
          main.alt = button.dataset.alt || '';
          gallery.querySelectorAll('[data-lc-gallery-thumb]').forEach((thumb) => {
            const selected = thumb === button;
            thumb.classList.toggle('is-active', selected);
            thumb.setAttribute('aria-pressed', String(selected));
          });
        });
      });
    });
  };

  const initializeStickyBuy = (root = document) => {
    root.querySelectorAll('[data-lc-sticky-buy]').forEach((bar) => {
      if (bar.dataset.initialized === 'true') return;
      bar.dataset.initialized = 'true';
      const scope = bar.closest('[data-lc-product-scope]');
      const source = scope?.querySelector('[data-lc-purchase-card], .lc-hero__purchase');
      if (!source || !('IntersectionObserver' in window)) return;
      const waitlist = document.getElementById('international-waitlist');
      let sourcePassed = false;
      let waitlistVisible = false;
      const sync = () => {
        bar.classList.toggle('is-visible', sourcePassed && !waitlistVisible);
        document.body.classList.toggle('lc-waitlist-in-view', waitlistVisible);
      };
      const observer = new IntersectionObserver(([entry]) => {
        sourcePassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sync();
      }, { threshold: 0.05 });
      observer.observe(source);
      if (waitlist) {
        const waitlistObserver = new IntersectionObserver(([entry]) => {
          waitlistVisible = entry.isIntersecting;
          sync();
        }, { threshold: 0.02 });
        waitlistObserver.observe(waitlist);
      }
    });
  };

  const initializeWaitlist = (root = document) => {
    root.querySelectorAll('[data-lc-waitlist-form]').forEach((form) => {
      if (form.dataset.initialized === 'true') return;
      form.dataset.initialized = 'true';
      const tags = form.querySelector('[data-lc-waitlist-tags]');
      const country = form.querySelector('[data-lc-waitlist-country]');
      const pet = form.querySelector('[data-lc-waitlist-pet]');
      const interest = form.querySelector('[data-lc-waitlist-interest]');
      const submit = form.querySelector('[data-lc-waitlist-submit]');
      const update = () => {
        const countryTag = `waitlist_country_${String(country?.value || '').toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
        const petTags = pet?.value === 'both' ? ['dog_owner', 'cat_owner', 'multi_pet'] : [pet?.value === 'cat' ? 'cat_owner' : 'dog_owner'];
        const interestTag = `interest_${String(interest?.value || 'not_sure').toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
        if (tags) tags.value = ['international_waitlist', 'shopify_storefront', countryTag, interestTag, ...petTags].join(',');
      };
      country?.addEventListener('change', update);
      pet?.addEventListener('change', update);
      interest?.addEventListener('change', update);
      form.addEventListener('submit', () => {
        update();
        if (submit) {
          const original = submit.textContent;
          submit.setAttribute('aria-busy', 'true');
          submit.textContent = submit.dataset.loadingText || original;
          window.setTimeout(() => {
            if (!document.contains(submit)) return;
            submit.removeAttribute('aria-busy');
            submit.textContent = original;
          }, 10000);
        }
      });
      update();
      form.querySelector('[data-lc-waitlist-success]')?.focus();
    });
  };

  const removeHamiltonOverlay = (root = document) => {
    root.querySelectorAll('.lc-hero__made').forEach((element) => element.remove());
  };

  const initialize = (root = document) => {
    removeHamiltonOverlay(root);
    initializeMenu(root);
    initializeLocalization(root);
    initializeReveal(root);
    initializeVariantSelectors(root);
    initializeProductForms(root);
    initializeQuickAdds(root);
    initializeGallery(root);
    initializeStickyBuy(root);
    initializeWaitlist(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
